import { z } from "zod";

/**
 * A WhatsApp group the linking user administers, as the "add okthx to your
 * groups" picker shows it. Only admins appear because okthx joins by invite
 * link, and only an admin can read a group's invite code.
 */
export type WhatsappPickerGroup = {
  /** The group JID, `…@g.us`. */
  id: string;
  subject: string;
  /** Member count. */
  size: number;
  /** okthx's number is already in the group, so there is nothing to add. */
  okthxIsMember: boolean;
};

export type WhatsappPickerGroupsResp = { groups: WhatsappPickerGroup[] };

/**
 * The most groups one pick can add okthx to. Each add is a write from the
 * user's own WhatsApp and a join from okthx's number; a small, paced batch
 * keeps both numbers clear of WhatsApp's bulk-activity flags.
 */
export const MAX_WHATSAPP_GROUP_ADDS = 25;

/**
 * The groups the picker starts with checked: the first `MAX_WHATSAPP_GROUP_ADDS`
 * okthx is not in yet, in the order the list arrives (most recently active
 * first).
 */
export function defaultWhatsappGroupSelection(groups: readonly WhatsappPickerGroup[]): string[] {
  return groups
    .filter((group) => !group.okthxIsMember)
    .slice(0, MAX_WHATSAPP_GROUP_ADDS)
    .map((group) => group.id);
}

/**
 * Whether the picker has anything to offer: a group the user administers that
 * okthx is not in. The post-link modal shows only when it does.
 */
export function hasWhatsappGroupToAdd(groups: readonly WhatsappPickerGroup[]): boolean {
  return groups.some((group) => !group.okthxIsMember);
}

/**
 * Why the group picker cannot run: no WhatsApp connector, or a linked session
 * that is not connected right now (it is still coming up, or was unlinked).
 */
export type WhatsappGroupPickerRefusalReason = "notConnected" | "notLive";

export type WhatsappGroupPickerRefusalResp = { reason: WhatsappGroupPickerRefusalReason };

export const joinWhatsappGroupsBodySchema = z.object({
  groupIds: z.array(z.string().endsWith("@g.us")).min(1).max(MAX_WHATSAPP_GROUP_ADDS),
});

export type JoinWhatsappGroupsBody = z.infer<typeof joinWhatsappGroupsBodySchema>;

/** How many of the requested groups okthx is being added to. */
export type JoinWhatsappGroupsResp = { queued: number };
